import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import {
  ContractPortalIcon,
  DashboardIcon,
  DocketEntryIcon,
  Manifestation,
  OfferLetterIcon,
  PaySlipIcon,
  PickupIcon,
  TripsheetIcon,
  UserManagementIcon,
} from "../assets/SvgConstant";

export const menuConfig = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: DashboardIcon,
    role: ["MANAGER", "EMPLOYEE"],
  },
  {
    name: "Pickup",
    path: "/pickup",
    icon: PickupIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "pickup",
    direction: "outbound",
  },
  {
    name: "Docket Entry",
    path: "/docket-entry",
    icon: DocketEntryIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "docket",
    direction: "outbound",
  },
  {
    name: "Manifestation",
    path: "/manifest",
    icon: Manifestation,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "manifest",
    direction: "outbound",
  },
  {
    name: "Trip Sheet",
    path: "/tripsheet",
    icon: TripsheetIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "tripSheet",
    direction: "outbound",
  },
  // inbound variants and delivery related items
  {
    name: "Trip Sheet (In)",
    path: "/tripsheet-in",
    icon: TripsheetIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "tripSheet",
    direction: "inbound",
  },
  {
    name: "Manifestation (In)",
    path: "/manifest-in",
    icon: Manifestation,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "manifest",
    direction: "inbound",
  },
  {
    name: "Out for Delivery",
    path: "/out-for-delivery",
    icon: ContractPortalIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "delivery",
    direction: "inbound",
  },
  {
    name: "Delivery Updation",
    path: "/delivery-updation",
    icon: ContractPortalIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "delivery",
    direction: "inbound",
  },
  {
    name: "POD Upload",
    path: "/pod-upload",
    icon: ContractPortalIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "pod",
    direction: "inbound",
  },
  {
    name: "Docket List",
    path: "/docket-list",
    icon: DocketEntryIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "docket",
    direction: "others",
  },
  {
    name: "Manifest List",
    path: "/manifest-list",
    icon: Manifestation,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "manifest",
    direction: "others",
  },
  {
    name: "Trip Sheet List",
    path: "/tripsheet-list",
    icon: TripsheetIcon,
    role: ["EMPLOYEE", "MANAGER"],
    permissionKey: "tripSheet",
    direction: "others",
  },
  {
    name: "Contract Portal",
    path: "/contract",
    icon: ContractPortalIcon,
    role: ["MANAGER"],
    direction: "others",
  },
  {
    name: "User Management",
    path: "/users",
    icon: UserManagementIcon,
    role: ["MANAGER"],
    direction: "others",
  },
  {
    name: "Offer Letters",
    path: "/offer-letter",
    icon: OfferLetterIcon,
    role: ["MANAGER"],
    direction: "hr",
  },
  {
    name: "Pay Slip",
    path: "/pay-slip",
    icon: PaySlipIcon,
    role: ["MANAGER"],
    direction: "hr",
  },
  {
    name: "Create Branch",
    path: "/create-branch",
    icon: ContractPortalIcon,
    role: ["MANAGER"],
    direction: "others",
  },
];

export function getMenuForRole(user) {
  if (!user) return [];

  // ✅ MANAGER → full access
  if (user.role === "MANAGER") {
    return menuConfig;
  }

  // ✅ EMPLOYEE → permission-based
  const permissions = user.permissions;

  return menuConfig.filter((item) => {
    // must be allowed for EMPLOYEE role
    if (!item.role?.includes("EMPLOYEE")) return false;

    // Dashboard is manager-only already
    if (!item.permissionKey) return true;

    // check inbound OR outbound permission
    return (
      permissions?.inbound?.[item.permissionKey] ||
      permissions?.outbound?.[item.permissionKey]
    );
  });
}

export const ProtectedRoute = ({
  children,
  allowedRoles,
  redirectPath = "/",
}) => {
  const location = useLocation();

  // redux
  const reduxUser = useSelector((s) => s.user?.user);
  const reduxToken = useSelector((s) => s.user?.token);

  let user = reduxUser;
  let token = reduxToken;

  // localStorage fallback
  if (!user || !token) {
    try {
      const persisted = localStorage.getItem("userState");
      if (persisted) {
        const parsed = JSON.parse(persisted);
        user = user || parsed.user;
        token = token || parsed.token;
      }

      if (!user) {
        const rawUser = localStorage.getItem("user");
        if (rawUser) user = JSON.parse(rawUser);
      }

      if (!token) {
        const rawToken = localStorage.getItem("token");
        if (rawToken) token = rawToken;
      }
    } catch (e) {
      console.warn("Auth read failed", e);
    }
  }

  // 🔴 NOT LOGGED IN
  if (!token || !user) {
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  const role = user?.role;

  // 🟡 ROLE NOT ALLOWED
  if (Array.isArray(allowedRoles) && !allowedRoles.includes(role)) {
    const roleHome = {
      MANAGER: "/dashboard",
      EMPLOYEE: "/dashboard",
    };

    return <Navigate to={roleHome[role] || "/"} replace />;
  }

  // 🟢 ACCESS GRANTED
  return <>{children}</>;
};

export const destinationOptions = [
  "BERHAMPUR",
  "KESINGA",
  "KORAPUT",
  "RAIGARH",
  "RAMGARH",
  "BOUDH",
  "NAYAGARH",
  "JEYPUR",
  "BOLANGIR",
  "KEONJHAR",
  "ANGUL",
  "BHUBANESWAR",
  "DHENKANAL",
  "KANTABANJI",
  "ADASPUR",
  "PURI",
  "DEOGARH",
  "SUNDERGARH",
  "BHADRAK",
  "BALASORE",
  "BARIPADA",
  "SALEPUR",
  "PANIKOILI",
  "BALIGUDA",
  "NIMAPARA",
  "BANAPUR",
  "JHARSUGUDA S",
  "KHORDHA",
  "DANAGADI",
  "JATANI",
  "BHAWANIPATNA",
  "JAJPUR TOWN",
  "PARADEEP",
  "KENDRAPARA",
  "RAYAGADA",
  "SAMBALPUR",
  "BARGARH",
  "BARBIL",
  "JAJPU ROAD",
  "ROURKELA",
  "KENOJHAR",
  "SONEPUR",
  "PHULBANI",
  "TANGI",
  "MAYURBHANJ",
  "GANJAM",
  "TALCHER",
  "KANDHAMAL",
  "BBSR",
  "BHOGARI",
  "ASKA",
  "JAJPUR ROAD",
  "RAMBHA",
  "CHANDOL",
  "JAGATSINGHPUR",
  "JUNAGARH",
  "RAHAMA",
  "G UDAIGIRI",
  "CHKITI PENTHO",
  "SORO",
  "SEMILIGUDA",
  "JANKIA",
  "SAKHIGOPAL",
  "JHARSUGUDA",
  "RAZABAZAR ROAD",
  "DARINGBADI",
  "G.UDAYAGIRI",
  "BRAHMAGIRI",
  "BALUGOAN",
  "JARAka",
  "TIHIDI",
  "RAJSUNAKHALA",
  "NAYAHAT",
  "KOTPAD",
  "MALKANIGIR",
  "ANANDAPUR",
  "JALESWAR",
  "LATHORE (KHAPARAKHOL)",
  "KHARIAR ROAD",
  "PIPILI",
  "UMORKOTE",
  "JAJPUR",
  "PIPIL",
  "CHANDANSWER",
  "JHARSUGUDA SANTOSH",
  "NAWRANGPUR",
  "KAMAKHYA",
  "BANPUR",
  "TITILAGARH",
  "KANTAPADA",
  "Gagapada",
  "NUAPADA",
  "RANIHAT",
  "CHANDANSWEAR",
  "BORIGUMA",
  "BALUGAON",
  "ADSPUR",
  "ODAGAON",
  "SUBARNPUR",
  "BHUBAN",
  "NUAPATNA",
  "DENGAUSTA",
  "CHATRAPUR",
  "NIMAPADA",
  "GAJAPATI",
  "CHIKITI PENTHO",
  "NAWARANGPUR",
  "TIKABALI",
  "PAPADAHANDI",
  "BHIMPUR",
  "KHARIER ROAD",
  "PBHUBANI",
  "KAPTIPADA",
  "NAYAGADA",
  "SAKIGOPAGAL",
  "RAHMAN",
  "KAVISURYANAGAR",
  "CHATRPUR",
  "BHANJANAGAR",
  "RAIKIA",
  "BORIGUM",
  "UMARKOTE",
  "HINJLIKUT",
  "BOLANGER",
  "SAMABALPUR",
  "CHANDANPUR",
  "KORAPUR",
  "DENGUSTA",
  "ATHGARH",
  "JHINKIRA",
  "CHANDKA",
  "SUNDARGARH",
  "KALAHANDI",
  "JEYPORE",
  "JAGATPUR",
  "CUTTACK",
  "MANGULI",
  "NAKHARA",
  "TIKKABALI",
  "DALTONGANJ",
  "CHIKITI PENTHA",
  "GHASIPUR",
  "TITALGARH",
  "NIALI",
  "RANPUR",
  "DENGASTA",
  "KURANGA",
  "RAJGANPUR",
  "DHARMGARH",
  "RAGHUNATHPUR",
  "CHOWDWAR",
  "HINJILICUT",
  "G.UDAYGIRI",
  "PAHAL",
  "RASULGARH",
  "HANSPAL",
  "BAISINGA",
  "SORADA",
  "UDALA",
  "GOPALPUR",
  "CHANDBALI",
  "KHURDA",
  "BOKARO",
  "DHARAMGARH",
  "BOLAGARH",
  "UMERKOTE",
  "GANGAPADA",
  "LATHORE",
  "JALESWER",
  "JORANDA",
  "JARKA",
  "CHOUDWAR",
  "PRADEEP",
  "TITLAGARH",
  "BERHAMPUR NARAYAN",
  "JARSUGUDA SANTOSH",
  "ROULKELA",
  "BARAGARH",
];

export const vendorTypeOptions = ["Self Fleet", "Vendor Fleet", "Market Fleet"];
export const vehicleModelOptions = [
  "AUTO",
  "TATA ACE",
  "8'FT",
  "10'FT",
  "14'FT",
  "20'FT",
  "22'FT",
  "24'FT",
  "32'FT",
];

export const routeOptions = [
  "BBI-RKL-BRL-KNJ-BBI",
  "BBI-JAJ-BDK-BLS-BPD-BBI",
  "BBI-JAJ-BDK-BLS-BPD-JLS-BBI",
  "BBI-JAJ-BDK-BLS-BPD-KNJ-BBI",
  "BBI-DKL-ANG-SMB-BBI",
  "BBI-DKL-ANG-SMB-JSD-BBI",
  "BBI-DKL-ANG-SMB-JSD-RKL-BBI",
  "BBI-DKL-ANG-SMB-JSD-RKL-BRL-KEJ-BBI",
  "BBI-DKL-ANG-SMB-BRH-BOL-BBI",
  "BBI-DKL-ANG-SMB-BRH-BOL-BWP-JEY-RGD-BRM-BBI",
  "BBI-BOL-BWP-JEY-RGD-BRM-BBI",
  "BBI-BRM-RGD-JEY-BWP-BOL-SON-BOU-PHU-NAY-BBI",
  "BBI-KHU-BRM-BBI",
  "BBI-KHU-BRM-NAY-BBI",
  "BBI-KEN-PDP-PURI-BBI",
  "BBI-PURI-PDP-KEN-BBI",
  "BBI-KHU-NAY-PHU-BBI",
  "BBI-KHU-NAY-BBI",
];
